import React, { useMemo, useRef, useState } from 'react';

import {
  closestCenter,
  DndContext,
  DragOverlay,
  MeasuringStrategy,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import AddIcon from 'icons/add.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { animated, useSpring } from 'react-spring';

import { useSortMasterActivityTradesMutation } from 'components/hooks/masterActivityTradeMutations';
import useMeasure from 'components/hooks/useMeasure';
import useSortSensors from 'components/hooks/useSortSensors';
import useWindowStorage from 'components/hooks/useWindowStorage';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import Tooltip from 'components/application/Tooltip';

import buildRiskAssessmentColumns from 'components/contentRegister/components/riskAssessmentColumns';
import DragPreviewTable from 'components/contentRegister/components/sortableTable/components/DragPreviewTable';
import GhostSlots from 'components/contentRegister/components/sortableTable/components/GhostSlots';
import SortableRow from 'components/contentRegister/components/sortableTable/components/SortableRow';
import {
  measureRow,
  moveId,
  uniformSlots,
} from 'components/contentRegister/components/sortableTable/helpers/sortables';
import { useContentRegisterContext } from 'components/contentRegister/contexts/ContentRegisterContext';
import {
  newRiskAssessmentPath,
  riskAssessmentPath,
} from 'components/contentRegister/helpers/paths';
import {
  returnStateKey,
  riskAssessmentRowElementId,
} from 'components/contentRegister/helpers/returnState';
import { useBreadBoard } from 'components/contexts/Toaster';

import { resourceShape } from 'components/helpers/serialisableResources';

const riskAssessmentCellClassName =
  'tw-h-12 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100';

export default function CategoryRiskAssessmentsPanel({
  columnCount,
  domainContentRegister,
  isImmediateExpansionEnabled,
  onMasterActivityToggle,
  onReorderMasterActivityTrades,
  onScrolledToReturnRow,
  orderedMasterActivityTradeIds,
  scrollToMasterActivityTradeId,
  selectedMasterActivityIds,
  trade,
}) {
  const { addInedibleToast } = useBreadBoard();
  const { riskRegisterPolicy } = useContentRegisterContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'contentRegister.components.CategoryRiskAssessmentsPanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [, setReturnState] = useWindowStorage(returnStateKey, {
    store: window.sessionStorage,
  });

  const sortMasterActivityTradesMutation =
    useSortMasterActivityTradesMutation();

  const sensors = useSortSensors();

  const [dragLayoutOptions, setDragLayoutOptions] = useState(null);
  const tableRef = useRef(null);
  const [bind, { height: panelHeight }] = useMeasure();

  const isRestoringScrollPosition = Boolean(scrollToMasterActivityTradeId);

  const springProps = useSpring({
    height: panelHeight,
    // Restoring a previous visit should land instantly, not play the expand animation.
    immediate: isImmediateExpansionEnabled || isRestoringScrollPosition,
    onRest: ({ finished }) => {
      if (!finished || panelHeight === 0 || !isRestoringScrollPosition) return;

      onScrolledToReturnRow();

      // requestAnimationFrame waits a repaint for onRest's settled height to reach the DOM.
      window.requestAnimationFrame(() => {
        document
          .getElementById(
            riskAssessmentRowElementId(scrollToMasterActivityTradeId),
          )
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
  });

  const columns = useMemo(
    () => buildRiskAssessmentColumns({ riskRegisterPolicy }),
    [riskRegisterPolicy],
  );

  const data = useMemo(
    () =>
      orderedMasterActivityTradeIds
        .map(
          (masterActivityTradeId) =>
            domainContentRegister.included.masterActivityTrade[
              masterActivityTradeId
            ],
        )
        .filter(Boolean),
    [
      domainContentRegister.included.masterActivityTrade,
      orderedMasterActivityTradeIds,
    ],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (masterActivityTrade) => masterActivityTrade.id,
    meta: {
      included: domainContentRegister.included,
      onMasterActivityToggle,
      onNavigate: (masterActivityTradeId) => {
        setReturnState({ masterActivityTradeId, tradeId: trade.id });
      },
      selectedMasterActivityIds,
      tradeId: trade.id,
      translations: {
        assignedTo: i18nTCommon('shared.assignedTo'),
        ref: i18nTCommon('shared.ref'),
        reorderName: (name) =>
          i18nTCommon('shared.reorderName', {
            interpolation: { escapeValue: false },
            name,
          }),
        reviewDate: i18nTCommon('projects.reviewDate'),
        riskAssessment: i18nTCommon('projects.riskAssessment'),
        selectName: (name) =>
          i18nTCommon('shared.selectName', {
            interpolation: { escapeValue: false },
            name,
          }),
        status: i18nTCommon('shared.status'),
      },
    },
  });

  const navigateToRiskAssessment = (masterActivityTrade) => {
    setReturnState({
      masterActivityTradeId: masterActivityTrade.id,
      tradeId: trade.id,
    });

    window.location = riskAssessmentPath(
      trade.id,
      masterActivityTrade.relationships.masterActivity.data.id,
    );
  };

  const handleDragStart = ({ active }) => {
    const { elementId } = active.data.current;
    const wrapper = tableRef.current;
    const row = measureRow(elementId);

    const firstRow = measureRow(
      riskAssessmentRowElementId(orderedMasterActivityTradeIds[0]),
    );

    if (!wrapper || !row || !firstRow) return;

    setDragLayoutOptions({
      activeId: active.id,
      columnWidths: row.columnWidths,
      slots: uniformSlots({
        count: orderedMasterActivityTradeIds.length,
        height: row.height,
        top: firstRow.top - wrapper.getBoundingClientRect().top,
      }),
      width: row.width,
    });
  };

  const handleDragEnd = ({ active, over }) => {
    setDragLayoutOptions(null);
    if (!over) return;

    const previousIds = orderedMasterActivityTradeIds;
    const masterActivityTradeIds = moveId(previousIds, active.id, over.id);
    if (masterActivityTradeIds === previousIds) return;

    onReorderMasterActivityTrades(masterActivityTradeIds);

    sortMasterActivityTradesMutation.mutate(
      { masterActivityTradeIds },
      {
        onError: () => {
          onReorderMasterActivityTrades(previousIds);
          addInedibleToast();
        },
      },
    );
  };

  const handleDragCancel = () => setDragLayoutOptions(null);

  const { rows } = table.getRowModel();

  const { isManageable } = trade.meta;

  return (
    <td
      className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-0'
      colSpan={columnCount}
    >
      <animated.div
        className='tw-overflow-hidden'
        style={{ height: springProps.height }}
      >
        <div {...bind}>
          <DndContext
            collisionDetection={closestCenter}
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            sensors={sensors}
          >
            <div className='tw-relative tw-isolate' ref={tableRef}>
              {dragLayoutOptions && (
                <GhostSlots slots={dragLayoutOptions.slots} />
              )}
              {/* Border-separate so that a dragged row's transform carries its cells with it, which border-collapse does not survive. */}
              <table
                className='tw-w-full tw-table-fixed tw-border-separate tw-border-spacing-0'
                data-element-name='category-risk-assessments'
              >
                <thead>
                  <tr className='tw-h-10 tw-bg-grey-050'>
                    {table.getFlatHeaders().map((header) => (
                      <th
                        className={classNames(
                          'tw-p-2 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900',
                          header.column.columnDef.meta?.classNames,
                        )}
                        key={header.id}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <SortableContext
                    items={rows.map((row) => row.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {rows.map((row) => {
                      const masterActivityTrade = row.original;

                      const {
                        id: masterActivityTradeId,
                        relationships: {
                          masterActivity: {
                            data: { id: masterActivityId },
                          },
                        },
                      } = masterActivityTrade;

                      return (
                        <SortableRow
                          className='tw-group tw-cursor-pointer tw-scroll-mt-[100px] tw-bg-white hover:tw-bg-grey-025'
                          data-element-name='risk-assessment-row'
                          data-master-activity-id={masterActivityId}
                          elementId={riskAssessmentRowElementId(
                            masterActivityTradeId,
                          )}
                          isSortable={isManageable}
                          key={masterActivityTradeId}
                          onClick={() => {
                            navigateToRiskAssessment(masterActivityTrade);
                          }}
                          sortableId={masterActivityTradeId}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              className={classNames(
                                riskAssessmentCellClassName,
                                cell.column.columnDef.meta?.classNames,
                              )}
                              key={cell.id}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </SortableRow>
                      );
                    })}
                  </SortableContext>
                  <tr className='tw-bg-white'>
                    <td
                      className='tw-h-12 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-pl-11'
                      colSpan={table.getAllLeafColumns().length}
                    >
                      <ConditionalWrapper
                        condition={!isManageable}
                        wrapper={(children) => (
                          <Tooltip
                            className='tooltip-dark--max-w-xs'
                            placement='top'
                            tooltip={i18nTCommon(
                              'riskRegister.noAccessToAllDivisionsUsingCategory',
                            )}
                            trigger='hover'
                          >
                            {children}
                          </Tooltip>
                        )}
                      >
                        <OutlinedButton
                          color='grey'
                          disabled={!isManageable}
                          href={
                            isManageable ?
                              newRiskAssessmentPath(trade.id)
                            : '#0'
                          }
                          size='sm'
                        >
                          <AddIcon
                            className='-tw-ml-2'
                            height={24}
                            width={24}
                          />
                          {i18nT('addRiskAssessment')}
                        </OutlinedButton>
                      </ConditionalWrapper>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <DragOverlay dropAnimation={null}>
              {dragLayoutOptions &&
                (() => {
                  const previewRow = rows.find(
                    (row) => row.id === dragLayoutOptions.activeId,
                  );

                  return (
                    previewRow && (
                      <DragPreviewTable
                        cellClassName={riskAssessmentCellClassName}
                        columns={table
                          .getFlatHeaders()
                          .map((header, index) => ({
                            id: header.id,
                            width: dragLayoutOptions.columnWidths?.[index],
                          }))}
                        row={previewRow}
                        width={dragLayoutOptions.width}
                      />
                    )
                  );
                })()}
            </DragOverlay>
          </DndContext>
        </div>
      </animated.div>
    </td>
  );
}

CategoryRiskAssessmentsPanel.propTypes = {
  columnCount: PropTypes.number.isRequired,
  domainContentRegister: PropTypes.shape({
    data: resourceShape('contentRegister').isRequired,
    included: PropTypes.shape({
      masterActivity: PropTypes.objectOf(resourceShape('masterActivity'))
        .isRequired,
      masterActivityTrade: PropTypes.objectOf(
        resourceShape('masterActivityTrade'),
      ).isRequired,
      personnel: PropTypes.objectOf(resourceShape('personnel')).isRequired,
      profilePhoto: PropTypes.objectOf(resourceShape('profilePhoto'))
        .isRequired,
      trade: PropTypes.objectOf(resourceShape('trade')).isRequired,
      user: PropTypes.objectOf(resourceShape('user')).isRequired,
    }).isRequired,
  }).isRequired,
  isImmediateExpansionEnabled: PropTypes.bool.isRequired,
  onMasterActivityToggle: PropTypes.func.isRequired,
  onReorderMasterActivityTrades: PropTypes.func.isRequired,
  onScrolledToReturnRow: PropTypes.func.isRequired,
  orderedMasterActivityTradeIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  scrollToMasterActivityTradeId: PropTypes.string,
  selectedMasterActivityIds: PropTypes.instanceOf(Set).isRequired,
  trade: resourceShape('trade').isRequired,
};
